import { useState } from 'react';
import { Box, Typography, Collapse } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import IconReusable from '../../../../components/IconReusable';
import { CustomButton } from '../../../../v2/MUI_custom_component';
import { downloadNpsTransactionStatementAction } from '../APIs/action';

const MetaRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
    <Typography variant="p3" sx={{ flex: 1, minWidth: 0 }}>
      {label}
    </Typography>
    <Typography variant="labelM" sx={{ flexShrink: 0, textAlign: 'right', whiteSpace: 'nowrap' }}>
      {value}
    </Typography>
  </Box>
);

const NPSPortfolioCard = ({ portfolio }) => {
  const theme = useTheme();
  const { gapGreen, gapRed } = theme.custom.colors;
  const [schemeOpen, setSchemeOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  if (!portfolio) return null;

  const isGain = portfolio.total_notional_gain_loss?.is_gain === true;
  const isPositiveXirr = portfolio.xirr >= 0;
  const chipColor = isGain ? gapGreen : gapRed;

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await downloadNpsTransactionStatementAction(
        portfolio.beneficiary_id,
        'NPS Transaction Statement.pdf',
      );
    } finally {
      setIsDownloading(false);
    }
  };

  if (portfolio.pran_generation_in_progress) {
    return (
      <Box
        sx={{
          flex: 1,
          bgcolor: 'background.default',
          borderRadius: 2,
          pt: 1,
          pb: 2,
          px: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          width: { md: '50%' }
        }}
      >
        <Typography variant="labelD" sx={{ px: 0.5 }}>
          {portfolio.beneficiary_name} NPS Portfolio
        </Typography>
        <Box
          sx={{
            background: (theme) => alpha(theme.palette.primary.main, 0.05),
            borderRadius: 1,
            px: 1,
            py: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Typography variant="labelM">PRAN</Typography>
          <Typography variant="h1" color={gapGreen}>
            {portfolio.pran_generation_message}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        bgcolor: 'background.default',
        borderRadius: 2,
        pt: 1,
        pb: 2,
        px: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        width: { md: '50%' }
      }}
    >
      <Typography variant="labelD" sx={{ px: 0.5 }}>
        {portfolio.beneficiary_name} NPS Portfolio
      </Typography>
      <Box
        sx={{
          background: (theme) => alpha(theme.palette.primary.main, 0.05),
          borderRadius: 1,
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        <Typography variant="labelM">Current Amount</Typography>

        <Typography variant="h1" color="primary.main">
          {portfolio.current_amount}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 1,
              py: 0.5,
              borderRadius: '32px',
              border: `1px solid ${chipColor}`,
              background: alpha(chipColor, 0.12),
              flexShrink: 0,
            }}
          >
            <Typography variant="labelD" sx={{ whiteSpace: 'nowrap' }}>
              {portfolio.total_notional_gain_loss?.value}
            </Typography>
          </Box>
          <Typography variant="labelD" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {portfolio.total_notional_gain_loss?.label?.toUpperCase()}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <MetaRow label="Invested Amount" value={portfolio.invested_amount} />
        <MetaRow label="Amount Withdrawn" value={portfolio.amount_withdrawn} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <Typography variant="p3" sx={{ flex: 1 }}>XIRR</Typography>
          <Typography variant="labelM" sx={{ flexShrink: 0 }}>{portfolio.xirr}%</Typography>
          <Box sx={{ flexShrink: 0, transform: isPositiveXirr ? 'none' : 'rotate(180deg)', display: 'flex' }}>
            <IconReusable name="Increase" width={15} height={9} color={isPositiveXirr? gapGreen : gapRed } />
          </Box>
        </Box>
      </Box>
      <Box
        onClick={() => setSchemeOpen((v) => !v)}
        sx={{ display: 'flex', alignItems: 'center', width: '100%', pt: 0.25, cursor: 'pointer' }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="labelM">Scheme</Typography>
          <Typography variant="p3" color="text.secondary">
            {portfolio.scheme_info?.scheme_name}
          </Typography>
        </Box>
        {schemeOpen
          ? <KeyboardArrowDownIcon sx={{ color: 'text.secondary', flexShrink: 0, transform: 'rotate(180deg)' }} />
          : <KeyboardArrowDownIcon sx={{ color: 'text.secondary', flexShrink: 0 }} />
        }
      </Box>
      <Collapse in={schemeOpen}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 0.5 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
            {['Units', 'NAV', 'Value'].map((h, i) => (
              <Box
                key={h}
                sx={{
                  bgcolor: 'grey.100',
                  px: 1,
                  py: 0.5,
                  textAlign: i === 2 ? 'right' : 'left',
                }}
              >
                <Typography variant="p3" color="text.secondary">{h}</Typography>
              </Box>
            ))}
            {[portfolio.scheme_info?.units, portfolio.scheme_info?.nav, portfolio.scheme_info?.value].map((v, i) => (
              <Box key={i} sx={{ px: 1, py: 0.5, textAlign: i === 2 ? 'right' : 'left' }}>
                <Typography variant="labelM">{v}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, px: 1 }}>
            <MetaRow label="Tier" value={portfolio.scheme_info?.tier} />
            <MetaRow label="PRAN" value={portfolio.scheme_info?.pran} />
            <MetaRow label="PAN" value={portfolio.scheme_info?.pan} />
            <MetaRow label="Beneficiary Name" value={portfolio.scheme_info?.beneficiary_name} />
          </Box>
          {portfolio.is_sot_visible && (
            <CustomButton
              variant="filled"
              color="primary"
              label="NPS Transaction Statement"
              leftIcon={FileDownloadOutlinedIcon}
              loading={isDownloading}
              onClick={handleDownload}
              fullWidth
            />
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default NPSPortfolioCard;
